import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import { EmtSelect, EmtTypography, EmtChip } from 'emt-components-v2'
import { DatePickerField } from '@/components/PetSitter/EmtCalendar/DatePickerField'
import { generateTimeOptions, timeSelectMenuProps } from './utils/timeUtils'
import { buildRecurrentDateList, MAX_RECURRENT_SLOTS } from './requestConfig'
import { useIsResponsive } from '@/hooks'

interface DateTimeFormProps {
  onSave: (instance: DateTimeInstance) => void
  initialValues: DateTimeInstance
  formType: 'promenade' | 'visite'
  title?: string
}

interface DateTimeInstance {
  date?: string
  dateStart?: string
  dateEnd?: string
  frequency?: 'once' | 'daily' | 'every_2_days' | 'every_3_days' | 'custom'
  customDays?: {
    scheduleEveryMonday: boolean
    scheduleEveryTuesday: boolean
    scheduleEveryWednesday: boolean
    scheduleEveryThursday: boolean
    scheduleEveryFriday: boolean
    scheduleEverySaturday: boolean
    scheduleEverySunday: boolean
  }
  timeEnd?: string
  serviceDayCount?: number
  serviceDuration?: string
  timePeriods?: Array<{ timeStart?: string; timeEnd?: string; serviceDuration?: string }>
}

const serviceDurationOptions = [
  { value: 'half_hour', label: '30 minutes' },
  { value: 'hour', label: '60 minutes' },
]

const serviceDayCountOptions = [
  { value: '1', label: '1 fois par jour' },
  { value: '2', label: '2 fois par jour' },
  { value: '3', label: '3 fois par jour' },
  { value: '4', label: '4 fois par jour' },
]

const DateTimeForm = forwardRef<any, DateTimeFormProps>(({ onSave, initialValues, formType }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const ensureTimePeriods = (v: DateTimeInstance, formType: 'promenade' | 'visite'): DateTimeInstance => {
    const count = v.serviceDayCount ?? 1
    const existing = Array.isArray(v.timePeriods) ? v.timePeriods : []

    // on garde ce qui existe déjà, sinon on seed des slots vides
    const next = Array.from({ length: count }, (_, i) => ({
      timeStart: existing[i]?.timeStart ?? '',
      serviceDuration: existing[i]?.serviceDuration ?? '',
    }))

    return {
      ...v,
      serviceDayCount: count,
      timePeriods: next,
    }
  }

  // ✅ init state
  const [values, setValues] = useState<DateTimeInstance>(() => ensureTimePeriods({ ...initialValues }, formType))

  // ✅ re-sync when initialValues change
  useEffect(() => {
    setValues(ensureTimePeriods({ ...initialValues }, formType))
    setErrors({ timePeriods: [] })

    // si tu relies aussi selectedTimeSlots à serviceDayCount
    setSelectedTimeSlots(initialValues.serviceDayCount ?? 1)
  }, [initialValues, formType])

  const [isMobile] = useIsResponsive()
  const [localSchedule, setLocalSchedule] = useState(() => {
    // Restore from initialValues.customDays if present
    if (initialValues && initialValues.customDays) {
      return { ...initialValues.customDays }
    }
    return {
      scheduleEveryMonday: false,
      scheduleEveryTuesday: false,
      scheduleEveryWednesday: false,
      scheduleEveryThursday: false,
      scheduleEveryFriday: false,
      scheduleEverySaturday: false,
      scheduleEverySunday: false,
    }
  })
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<number>(
    formType === 'promenade' ? initialValues.serviceDayCount || 1 : initialValues.serviceDayCount || 1,
  )
  const lastEmittedRef = useRef<string>('')

  const dayChips = [
    { label: isMobile ? 'L' : 'Lun', field: 'scheduleEveryMonday', scheduled: localSchedule.scheduleEveryMonday },
    { label: isMobile ? 'M' : 'Mar', field: 'scheduleEveryTuesday', scheduled: localSchedule.scheduleEveryTuesday },
    {
      label: isMobile ? 'M' : 'Mer',
      field: 'scheduleEveryWednesday',
      scheduled: localSchedule.scheduleEveryWednesday,
    },
    { label: isMobile ? 'J' : 'Jeu', field: 'scheduleEveryThursday', scheduled: localSchedule.scheduleEveryThursday },
    { label: isMobile ? 'V' : 'Ven', field: 'scheduleEveryFriday', scheduled: localSchedule.scheduleEveryFriday },
    { label: isMobile ? 'S' : 'Sam', field: 'scheduleEverySaturday', scheduled: localSchedule.scheduleEverySaturday },
    { label: isMobile ? 'D' : 'Dim', field: 'scheduleEverySunday', scheduled: localSchedule.scheduleEverySunday },
  ]

  const [errors, setErrors] = useState<Record<string, any>>({
    timePeriods: [], // Initialize errors for time periods
    frequency: '',
    dateStart: '',
    dateEnd: '',
    timeStart: '',
    timeEnd: '',
    serviceDayCount: '',
    maxDates: '',
  })

  useEffect(() => {
    setValues({
      ...initialValues,
    })
    // Reset errors when formType changes
    setErrors({ timePeriods: [] })
    // Restore localSchedule from initialValues.customDays if present
    if (initialValues && initialValues.customDays) {
      setLocalSchedule({ ...initialValues.customDays })
    } else {
      setLocalSchedule({
        scheduleEveryMonday: false,
        scheduleEveryTuesday: false,
        scheduleEveryWednesday: false,
        scheduleEveryThursday: false,
        scheduleEveryFriday: false,
        scheduleEverySaturday: false,
        scheduleEverySunday: false,
      })
    }
    setSelectedTimeSlots(
      formType === 'promenade' ? initialValues.serviceDayCount || 1 : initialValues.serviceDayCount || 1,
    )
  }, [initialValues, formType])

  const onFieldChange = (key: string, value: any, index?: number) => {
    setValues((prevValues) => ({ ...prevValues, [key]: value }))
    checkField(key, value, index)
  }
  const checkField = (key: string, value: any = values[key], index?: number, showErrors = true): boolean => {
    let error = ''
    switch (key) {
      case 'frequency':
        if (!value) {
          error = 'Veuillez sélectionner une fréquence'
        }
        break
      case 'dateStart':
        if (!value) {
          error = 'Veuillez sélectionner une date de début'
        }
        break
      case 'dateEnd':
        if (!value && values.frequency !== 'once') {
          error = 'Veuillez sélectionner une date de fin'
        }
        break
      case 'timeStart':
        if (!value && formType !== 'visite' && formType !== 'promenade') {
          error = 'Veuillez sélectionner une heure de début'
        }
        break
      case 'timeEnd':
        if (!value && formType !== 'visite' && formType !== 'promenade') {
          error = 'Veuillez sélectionner une heure de fin'
        }
        break
      case 'serviceDayCount':
        if (!value) {
          error = 'Veuillez sélectionner une quantité de visites quotidienne'
        }
        break
      case 'timePeriods':
        // Check each time period for errors
        const newTimePeriodErrors = (value || []).map((timePeriod: any) => {
          const periodErrors: {
            timeStart?: string
            timeEnd?: string
            serviceDuration?: string
          } = {}

          // Check for timeStart error
          if (!timePeriod.timeStart) {
            periodErrors.timeStart = 'Veuillez sélectionner une heure de début'
          }

          // Check for timeEnd or serviceDuration error
          if (!timePeriod.timeEnd && !timePeriod.serviceDuration && !timePeriod.serviceDuration) {
            periodErrors.timeEnd = 'Veuillez sélectionner une heure de fin'
            periodErrors.serviceDuration = 'Veuillez sélectionner une durée de visite'
            periodErrors.serviceDuration = 'Veuillez sélectionner une durée de promenade'
          }

          return periodErrors
        })
        // Update the errors state for timePeriods
        if (typeof index === 'number') {
          const periodErrors: any = {}

          if (showErrors) {
            setErrors((prev) => ({
              ...prev,
              timePeriods: prev.timePeriods.map((err: any, i: number) => (i === index ? periodErrors : err)),
            }))
          }
          return Object.keys(periodErrors).length === 0
        } else {
          if (showErrors) {
            setErrors((prevErrors) => ({ ...prevErrors, timePeriods: newTimePeriodErrors }))
          }

          // Check if there are any errors in the time periods
          const hasTimePeriodErrors = newTimePeriodErrors.some(
            (periodError) => periodError.timeStart || periodError.timeEnd,
          )
          return !hasTimePeriodErrors
        }
      default:
        break
    }

    if (key !== 'timePeriods' && showErrors) {
      setErrors((prevErrors) => ({ ...prevErrors, [key]: error }))
    }

    return !error
  }

  // build the date list once (no timezones)
  const buildDateList = (): string[] => buildRecurrentDateList({ ...values, customDays: localSchedule })

  // the limit validator
  const MAX_SLOTS = MAX_RECURRENT_SLOTS

  const countValidSlots = () =>
    (values.timePeriods || []).filter((tp) => tp && tp.timeStart && (tp.serviceDuration || tp.serviceDuration)).length

  const validateSlotsLimit = (): boolean => {
    const datesCount = buildDateList().length
    const slotsPerDay = countValidSlots()
    const total = datesCount * slotsPerDay

    if (!datesCount || !slotsPerDay) {
      // Let field-level validators handle “missing date/slot”
      if (errors.maxDates) setErrors((prev) => ({ ...prev, maxDates: '' }))
      return true
    }

    if (total > MAX_SLOTS) {
      setErrors((prev) => ({
        ...prev,
        maxDates: `Vous avez sélectionné un total de ${total} ${
          formType === 'promenade' ? 'promenades' : 'visites'
        }. Le maximum autorisé est  ${MAX_SLOTS}, réduisez le nombre total de jours ou la fréquence. Vous pourrez faire d’autres demandes séparément si vous avez plus de besoins. `,
      }))
      return false
    }

    if (errors.maxDates) setErrors((prev) => ({ ...prev, maxDates: '' }))
    return true
  }

  // 5) include the limit check in your existing validate flow
  const validateForm = (showErrors = true) => {
    let isValid = true
    const fieldsToValidate = Object.keys(values)
    fieldsToValidate.forEach((key) => {
      if (!checkField(key, (values as any)[key], undefined, showErrors)) isValid = false
    })
    if (!validateSlotsLimit()) isValid = false
    return isValid
  }

  // Auto check
  useEffect(() => {
    const snapshot = JSON.stringify({
      ...values,
      customDays: values.frequency === 'custom' ? localSchedule : undefined,
    })
    const ok = validateForm(false)
    let valuesToSave = values
    if (values.frequency === 'custom') {
      valuesToSave = { ...values, customDays: { ...localSchedule } }
    }
    if (ok && snapshot !== lastEmittedRef.current) {
      onSave(valuesToSave)
      lastEmittedRef.current = snapshot
    }
  }, [values, localSchedule])

  const updateTimePeriod = (
    index: number,
    key: 'timeStart' | 'timeEnd' | 'serviceDuration' | 'serviceDuration',
    value: string,
  ) => {
    const newTimePeriods = [...(values.timePeriods || [])]
    newTimePeriods[index] = {
      ...newTimePeriods[index],
      [key]: value,
    }
    onFieldChange('timePeriods', newTimePeriods, index)
  }

  const getMinDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  const getMinEndDate = () => {
    if (values.dateStart) {
      return values.dateStart
    }
    return getMinDate()
  }

  const getMaxEndDate = () => {
    if (!values.dateStart) return ''
    const startDate = new Date(values.dateStart)
    const maxDate = new Date(startDate)
    maxDate.setMonth(maxDate.getMonth() + 3)
    return maxDate.toISOString().split('T')[0]
  }

  const getTimeOptions = (selectedDate?: string) => {
    return generateTimeOptions('00:00', '23:30', 30, selectedDate)
  }

  const getFrequencyOptions = () => {
    return [
      { value: 'once', label: 'Un seul jour' },
      { value: 'daily', label: 'Tous les jours' },
      { value: 'every_2_days', label: 'Tous les 2 jours' },
      { value: 'every_3_days', label: 'Tous les 3 jours' },
      { value: 'custom', label: 'Choisir les jours de la semaine' },
    ]
  }

  // const hasErrors = () => {
  //   // Check for any direct errors
  //   const hasDirectErrors = Object.keys(errors).some((key) => key !== 'timePeriods' && errors[key])

  //   // Check for errors in timePeriods
  //   const hasTimePeriodErrors = errors.timePeriods?.some((periodError: any) => Object.keys(periodError).length > 0)

  //   return hasDirectErrors || hasTimePeriodErrors
  // }

  useImperativeHandle(ref, () => ({
    validateAndSave: async () => {
      // keep old behaviour if someone still calls this
      if (validateForm()) {
        let valuesToSave = values
        if (values.frequency === 'custom') {
          valuesToSave = { ...values, customDays: { ...localSchedule } }
        }
        onSave(valuesToSave)
        setValues({
          dateStart: '',
          dateEnd: '',
          frequency: 'once',
          serviceDayCount: undefined,
          serviceDuration: '',
          timePeriods: [],
        })
        setErrors({ timePeriods: [] })
        return true
      }
      return false
    },
    container: containerRef,

    validateOnly: () => validateForm(),

    // NEW: expand without saving
    expandInstances: (service: 'promenade' | 'visite') => {
      // hard-stop if over the limit
      if (!validateSlotsLimit()) return []

      const days = buildDateList()
      const instances: DateTimeInstance[] = []
      for (const date of days) {
        for (const slot of values.timePeriods || []) {
          if (!slot.timeStart) continue
          if (service === 'promenade') {
            instances.push({
              date,
              timePeriods: [{ timeStart: slot.timeStart, serviceDuration: slot.serviceDuration }],
            })
          } else {
            instances.push({
              date,
              timePeriods: [{ timeStart: slot.timeStart, serviceDuration: slot.serviceDuration }],
            })
          }
        }
      }
      return instances
    },
  }))

  const handleChipClick = async (field: string) => {
    const updatedSchedule = {
      ...localSchedule,
      [field]: !localSchedule[field as keyof typeof localSchedule],
    }

    setLocalSchedule(updatedSchedule)
  }

  const handleWalkCountChange = (value: string) => {
    const count = parseInt(value)
    setSelectedTimeSlots(count)
    // Save both serviceDayCount and serviceDayCount in values
    onFieldChange('serviceDayCount', count)
    onFieldChange('serviceDayCount', count)
    const currentTimePeriods = values.timePeriods || []
    const newTimePeriods = Array(count)
      .fill(null)
      .map((_, index) => {
        return index < currentTimePeriods.length
          ? currentTimePeriods[index]
          : {
              timeStart: '',
              serviceDuration: '',
            }
      })

    onFieldChange('timePeriods', newTimePeriods)
    setErrors((prev) => ({ ...prev, timePeriods: Array(count).fill({}) }))
  }

  return (
    <>
      <Box
        ref={containerRef}
        // sx={{ border: '1px solid #AE59C6', borderRadius: '18px', padding: '18px 24px' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* <EmtTypography variant="body1" fontWeight="700">
              À quelle fréquence ?{' '}
            </EmtTypography> */}
          <EmtSelect
            // @ts-expect-error
            color="purple"
            inputLabel={`Fréquence des ${formType === 'promenade' ? 'promenades' : 'visites'}`}
            options={getFrequencyOptions()}
            value={values.frequency}
            onChange={(value) => onFieldChange('frequency', value as any)}
            error={!!errors.frequency}
            helperText={errors.frequency}
          />
          {values.frequency === 'custom' && (
            <Box sx={{ display: 'flex', gap: '6px', flexShrink: '1', maxWidth: '100%' }}>
              {dayChips.map((chip, idx) => (
                <EmtChip
                  key={idx}
                  sx={{
                    border: `1px solid ${chip.scheduled ? '#19BA92' : '#77757F'}`,
                    width: '100%',
                    height: '40px',
                    borderRadius: '100vmax',
                    cursor: 'pointer',
                    backgroundColor: chip.scheduled ? '#E9FDF8' : '#ffffff00',
                    '&:hover': {
                      backgroundColor: chip.scheduled ? '#E9FDF8' : '#f0f0f0',
                    },
                  }}
                  onClick={() => handleChipClick(chip.field)}
                  color={chip.scheduled ? 'successlight' : 'default'}
                  label={chip.label}
                />
              ))}
            </Box>
          )}
          {/* <EmtTypography variant="body1" fontWeight="700">
              {values.frequency === 'once' ? 'À quelle date ? ' : 'À quelle dates ? '}
              {values.frequency !== 'once' && (
                <span style={{ fontWeight: '400', color: '#4A4754', fontSize: '14px' }}>(3 mois maximum)</span>
              )}
            </EmtTypography> */}
          <Box
            sx={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: values.frequency !== 'once' ? '1fr 1fr' : '1fr',
            }}
          >
            <DatePickerField
              label={values.frequency === 'once' ? 'Date' : 'Date de début'}
              value={values.dateStart}
              onChange={(newStartDate) => {
                const minDate = getMinDate()
                if (newStartDate >= minDate) {
                  onFieldChange('dateStart', newStartDate)
                  // Reset end date if it exists and is before new start date
                  if (values.dateEnd && values.dateEnd < newStartDate) {
                    onFieldChange('dateEnd', '')
                  }
                  // Clear any previous error
                  setErrors((prev) => ({ ...prev, dateStart: '' }))
                } else {
                  // Set error for past date
                  setErrors((prev) => ({ ...prev, dateStart: 'La date ne peut pas être dans le passé' }))
                }
              }}
              error={!!errors.dateStart}
              helperText={errors.dateStart}
              min={getMinDate()}
            />
            {values.frequency !== 'once' && (
              <DatePickerField
                label="Date de fin"
                value={values.dateEnd}
                onChange={(selectedEndDate) => {
                  const minDate = getMinDate()
                  const minEndDate = getMinEndDate()
                  const maxEndDate = getMaxEndDate()

                  if (selectedEndDate > maxEndDate) {
                    setErrors((prev) => ({
                      ...prev,
                      dateEnd: 'La date de fin ne peut pas dépasser 3 mois après la date de début',
                    }))
                  } else if (selectedEndDate >= minEndDate) {
                    onFieldChange('dateEnd', selectedEndDate)
                    setErrors((prev) => ({ ...prev, dateEnd: '' }))
                  } else if (selectedEndDate < minDate) {
                    setErrors((prev) => ({ ...prev, dateEnd: 'La date ne peut pas être dans le passé' }))
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      dateEnd: 'La date de fin doit être postérieure à la date de début',
                    }))
                  }
                }}
                error={!!errors.dateEnd}
                helperText={errors.dateEnd}
                min={getMinEndDate()}
                max={getMaxEndDate()}
              />
            )}
          </Box>
          {/* <EmtTypography variant="body1" fontWeight="700">
              Quel(s) horaire(s) souhaitez vous réserver ?
            </EmtTypography> */}
          <EmtSelect
            // @ts-expect-error
            color="purple"
            inputLabel={`Nombre de ${formType === 'promenade' ? 'promenades' : 'visites'} par jour sélectionné`}
            options={serviceDayCountOptions}
            value={selectedTimeSlots.toString()}
            onChange={(value) => handleWalkCountChange(value as string)}
          />
          {values.timePeriods?.map((timePeriod, index) => (
            <Box key={index} sx={{ display: 'flex', gap: '10px' }}>
              <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                <EmtSelect
                  // @ts-expect-error
                  color="purple"
                  inputLabel={values.timePeriods && values.timePeriods.length > 1 ? `Horaire ${index + 1}` : 'Horaire'}
                  options={getTimeOptions(values.dateStart)}
                  MenuProps={timeSelectMenuProps}
                  value={timePeriod.timeStart}
                  onChange={(value) => updateTimePeriod(index, 'timeStart', value as any)}
                  error={!!errors.timePeriods[index]?.timeStart}
                  helperText={errors.timePeriods[index]?.timeStart}
                />
              </Box>
              <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                <EmtSelect
                  // @ts-expect-error
                  color="purple"
                  inputLabel={`Durée de la ${formType === 'promenade' ? 'promenade' : 'visite'} `}
                  options={serviceDurationOptions}
                  value={timePeriod.serviceDuration}
                  onChange={(value) => updateTimePeriod(index, 'serviceDuration', value as any)}
                  error={!!errors.timePeriods[index]?.serviceDuration}
                  helperText={errors.timePeriods[index]?.serviceDuration}
                />
              </Box>
            </Box>
          ))}
          {errors.maxDates && (
            <EmtTypography variant="caption" color="error">
              {errors.maxDates}
            </EmtTypography>
          )}
        </Box>
      </Box>
    </>
  )
})

DateTimeForm.displayName = 'DateTimeForm'

export default DateTimeForm
